import type {
  ClientDetailQuery,
  ClientDetailQueryVariables,
} from "@src/__generated__/urql-graphql";
import type { TRefetch } from "@src/utils/apolloHelpers";
import { assert } from "@src/utils/types";
import { remove } from "lodash";
import { computed, makeObservable, observable } from "mobx";
import { FormClientDetail } from "./FormClientDetail";
import {
  ActiveExchangeRateModel,
  ClientModel,
  ContactModel,
  CurrencyModel,
  ExchangeRateModel,
} from "./models";

type TCountry = NonNullable<ClientDetailQuery["countrySimpleMap"]>[0];
type TCurrency = NonNullable<ClientDetailQuery["currencies"]>[0];

export class Store {
  refetch?: TRefetch<ClientDetailQueryVariables, ClientDetailQuery>;

  @observable client!: ClientModel;

  @observable contacts: ContactModel[] = [];

  @observable.ref countries: TCountry[] = [];

  @observable.ref availableCurrencies: TCurrency[] = [];

  @observable ratesGroupedByCurrency: CurrencyModel[] = [];

  @observable activeRates: ActiveExchangeRateModel[] = [];

  form = new FormClientDetail();

  constructor() {
    makeObservable(this);
  }

  fromClientDetailQuery(src: ClientDetailQuery) {
    assert(src.client);
    this.client = new ClientModel(src.client);
    this.contacts = src.client.contacts.map((i) => new ContactModel(i)) ?? [];
    this.countries = src.countrySimpleMap;
    this.availableCurrencies = src.currencies ?? [];
    this.form.responsible_person_id.onChange(
      src.client.responsiblePerson?.id || "",
    );
    this.form.other_responsible_persons_ids.onChange(
      src.client?.otherResponsiblePersons?.map((person) => person.id) || [],
    );
    this.activeRates = src.client.currencyExchangeRates.map(
      (i) => new ActiveExchangeRateModel(i),
    );
    this.ratesGroupedByCurrency =
      src.client.currencyExchangeRatesStructured.map((i) => {
        return new CurrencyModel(
          i.currency,
          i.exchangeRates.map((j) => {
            return new ExchangeRateModel(j, i.currency);
          }),
        );
      });
  }

  static create() {
    return new Store();
  }

  deleteContactById(id: string) {
    remove(this.contacts, { id });
  }

  deleteBillingAddressById(id: string) {
    remove(this.client.billingEntities, { id });
  }

  deleteBrandById(id: string) {
    remove(this.client.brands, { id });
  }

  deleteExchangeRateById(id: string) {
    for (const rates of this.ratesGroupedByCurrency) {
      remove(rates.rates, { id });
    }
    remove(this.activeRates, { id });
  }

  @computed get billingEntitiesOptions() {
    return {
      [this.client.id]:
        this.client.billingEntities.map((i) => ({
          label: i.internal_name,
          value: i.id,
        })) ?? [],
    };
  }
}
